/**
 * =====================================================
 * NOTIFICATION TEMPLATES & AUTOMATION SETTINGS
 * =====================================================
 * Admin page for managing push notification templates
 * and configuring automated notification triggers.
 * 
 * Features:
 * - Create/Edit/Delete notification templates
 * - Map templates to order status events
 * - Enable/disable automated notifications per status
 * - Preview notification with variable interpolation
 * - Deep link configuration
 */

import React, { useState, useEffect, useCallback } from "react";
import Select from 'react-select';
import { FaBell, FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff, FaSave, FaEye, FaTimes, FaCheckCircle, FaExclamationTriangle, FaMobileAlt } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { getNotificationTemplates, createNotificationTemplate, updateNotificationTemplate, deleteNotificationTemplate, getNotificationSettings, updateNotificationSettings } from "../../services/ClientService";
import ConfirmModal from "../ConfirmModal";


const ORDER_STATUSES = ['Received', 'Confirmed', 'Shipped', 'Out For Delivery', 'Delivered', 'Cancelled'];

const CHANNELS = [
    { value: 'order_status', label: 'Order Status', description: 'Triggered when order status changes' },
    { value: 'free_cash', label: 'Free Cash', description: 'Free Cash wallet credits and expiry reminders' },
    { value: 'promotion', label: 'Promotion', description: 'Promotional notifications' },
    { value: 'custom', label: 'Custom', description: 'Custom notification templates' },
];

const FREE_CASH_EVENTS = [
    { key: 'segment_load', label: 'Load by order milestone', description: 'When Free Cash is loaded via segment (first/second order)' },
    { key: 'custom_load', label: 'Custom Free Cash', description: 'When Free Cash is loaded to manually selected customers' },
    { key: 'expiry_reminder', label: 'Before cash expires', description: 'Reminder before wallet credit expires' },
];

const STATUS_COLORS = {
    'Received': 'bg-blue-100 text-blue-700',
    'Confirmed': 'bg-indigo-100 text-indigo-700',
    'Shipped': 'bg-purple-100 text-purple-700',
    'Out For Delivery': 'bg-orange-100 text-orange-700',
    'Delivered': 'bg-green-100 text-green-700',
    'Cancelled': 'bg-red-100 text-red-700',
};

const TEMPLATE_VARIABLES = [
    { var: '{{orderNumber}}', desc: 'Order ID (e.g., ZU_ORD_00001)' },
    { var: '{{customerName}}', desc: 'Customer name' },
    { var: '{{statusTo}}', desc: 'New status' },
    { var: '{{statusFrom}}', desc: 'Previous status' },
    { var: '{{itemName}}', desc: 'Item name(s) affected (e.g., "Mango, Apple")' },
];

const FREE_CASH_TEMPLATE_VARIABLES = [
    { var: '{{customerName}}', desc: 'Customer name' },
    { var: '{{freeCashAmount}}', desc: 'Free Cash amount credited (₹)' },
    { var: '{{walletBalance}}', desc: 'Total active wallet balance after credit (₹)' },
    { var: '{{minCartValue}}', desc: 'Minimum cart value to use Free Cash (₹)' },
    { var: '{{expiresAt}}', desc: 'Full expiry date & time (e.g., 09 Jun 2026 01:10 PM)' },
    { var: '{{expiryDate}}', desc: 'Expiry date only' },
    { var: '{{expiryTime}}', desc: 'Expiry time only' },
    { var: '{{segmentLabel}}', desc: 'Segment label (First order / Second order)' },
    { var: '{{minutesRemaining}}', desc: 'Minutes until expiry (expiry reminder only)' },
];

const FREE_CASH_PREVIEW_DATA = {
    customerName: 'John Doe',
    freeCashAmount: '50',
    walletBalance: '150',
    minCartValue: '299',
    expiresAt: '09 Jun 2026 01:10 PM',
    expiryDate: '09 Jun 2026',
    expiryTime: '01:10 PM',
    segmentLabel: 'First order',
    minutesRemaining: '3',
};

const NotificationTemplates = () => {
    const [templates, setTemplates] = useState([]);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('automation'); // 'automation' | 'templates'
    
    // Template form state
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [templateForm, setTemplateForm] = useState({
        name: '', channel: 'order_status', event: '', title: '', body: '', imageUrl: '', isDefault: false,
        deepLink: { screen: 'OrderDetails', params: {} },
    });
    
    // Preview state
    const [showPreview, setShowPreview] = useState(false);

    // Dynamic preview data — statusTo reflects the selected event/status in the template form
    const getPreviewData = useCallback((event, channel) => {
        if (channel === 'free_cash') {
            return { ...FREE_CASH_PREVIEW_DATA, segmentLabel: event === 'segment_load' ? 'First order' : FREE_CASH_PREVIEW_DATA.segmentLabel };
        }
        const statusTo = event || 'Shipped';
        const statusIndex = ORDER_STATUSES.indexOf(statusTo);
        const statusFrom = statusIndex > 0 ? ORDER_STATUSES[statusIndex - 1] : 'Received';
        return {
            orderNumber: 'ZU_ORD_00042',
            customerName: 'John Doe',
            statusTo,
            statusFrom,
            itemName: 'Organic Mango, Fresh Apple',
        };
    }, []);

    // Snackbar
    const [snackbar, setSnackbar] = useState({ show: false, message: '', type: 'success' });
    const [confirmModal, setConfirmModal] = useState({ visible: false, message: '', onConfirm: null });

    const showSnackbar = (message, type = 'success') => {
        setSnackbar({ show: true, message, type });
        setTimeout(() => setSnackbar({ show: false, message: '', type: 'success' }), 3000);
    };

    // Fetch data
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [templatesRes, settingsRes] = await Promise.all([
                getNotificationTemplates(),
                getNotificationSettings(),
            ]);
            if (templatesRes.success) setTemplates(templatesRes.data);
            if (settingsRes.success) setSettings(settingsRes.data);
        } catch (error) {
            console.error('Error fetching notification data:', error);
            showSnackbar('Failed to load notification data', 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    // =====================================================
    // AUTOMATION SETTINGS HANDLERS
    // =====================================================

    const handleToggleChannel = async (channel) => {
        const updatedSettings = { ...settings };
        if (!updatedSettings.automations) updatedSettings.automations = {};
        if (!updatedSettings.automations[channel]) {
            updatedSettings.automations[channel] = { enabled: false, events: {} };
        }
        updatedSettings.automations[channel].enabled = !updatedSettings.automations[channel].enabled;
        setSettings(updatedSettings);
        await saveSettings(updatedSettings.automations);
    };

    const handleToggleEvent = async (channel, event) => {
        const updatedSettings = { ...settings };
        if (!updatedSettings.automations) updatedSettings.automations = {};
        if (!updatedSettings.automations[channel]) {
            updatedSettings.automations[channel] = { enabled: true, events: {} };
        }
        if (!updatedSettings.automations[channel].events) {
            updatedSettings.automations[channel].events = {};
        }
        if (!updatedSettings.automations[channel].events[event]) {
            updatedSettings.automations[channel].events[event] = {
                active: false,
                templateId: null,
                ...(channel === 'free_cash' && event === 'expiry_reminder' ? { minutesBefore: 3 } : {}),
            };
        }
        const eventConfig = updatedSettings.automations[channel].events[event];
        eventConfig.active = !eventConfig.active;
        updatedSettings.automations[channel].events[event] = eventConfig;
        setSettings(updatedSettings);
        await saveSettings(updatedSettings.automations);
    };

    const handleAssignTemplate = async (channel, event, templateId) => {
        const updatedSettings = { ...settings };
        if (!updatedSettings.automations?.[channel]?.events?.[event]) return;
        updatedSettings.automations[channel].events[event].templateId = templateId || null;
        setSettings(updatedSettings);
        await saveSettings(updatedSettings.automations);
    };

    const handleExpiryMinutesChange = async (minutes) => {
        const updatedSettings = { ...settings };
        if (!updatedSettings.automations?.free_cash?.events?.expiry_reminder) return;
        updatedSettings.automations.free_cash.events.expiry_reminder.minutesBefore = Math.max(1, Number(minutes) || 3);
        setSettings(updatedSettings);
        await saveSettings(updatedSettings.automations);
    };

    const saveSettings = async (automations) => {
        setSaving(true);
        try {
            const res = await updateNotificationSettings({ automations });
            if (res.success) {
                showSnackbar('Settings saved successfully');
            } else {
                showSnackbar('Failed to save settings', 'error');
            }
        } catch (error) {
            showSnackbar('Error saving settings', 'error');
        } finally {
            setSaving(false);
        }
    };

    // =====================================================
    // TEMPLATE CRUD HANDLERS
    // =====================================================

    const handleCreateTemplate = () => {
        setEditingTemplate(null);
        setTemplateForm({
            name: '', channel: 'order_status', event: '', title: '', body: '', imageUrl: '', isDefault: false,
            deepLink: { screen: 'OrderDetails', params: {} },
        });
        setShowTemplateModal(true);
    };

    const handleEditTemplate = (template) => {
        setEditingTemplate(template);
        setTemplateForm({
            name: template.name || '',
            channel: template.channel || 'order_status',
            event: template.event || '',
            title: template.title || '',
            body: template.body || '',
            imageUrl: template.imageUrl || '',
            isDefault: template.isDefault || false,
            deepLink: template.deepLink || { screen: 'OrderDetails', params: {} },
        });
        setShowTemplateModal(true);
    };

    const handleSaveTemplate = async () => {
        if (!templateForm.name || !templateForm.title || !templateForm.body) {
            showSnackbar('Name, title, and body are required', 'error');
            return;
        }
        if (templateForm.channel === 'free_cash' && !templateForm.event) {
            showSnackbar('Please select a Free Cash event for this template', 'error');
            return;
        }
        setSaving(true);
        try {
            let res;
            if (editingTemplate) {
                res = await updateNotificationTemplate({
                    _id: editingTemplate._id,
                    ...templateForm,
                });
            } else {
                res = await createNotificationTemplate(templateForm);
            }
            if (res.success) {
                showSnackbar(editingTemplate ? 'Template updated' : 'Template created');
                setShowTemplateModal(false);
                fetchData();
            } else {
                showSnackbar(res.message || 'Failed to save template', 'error');
            }
        } catch (error) {
            showSnackbar('Error saving template', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteTemplate = (templateId) => {
        setConfirmModal({
            visible: true,
            message: 'Are you sure you want to delete this template?',
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, visible: false }));
                try {
                    const res = await deleteNotificationTemplate({ _id: templateId });
                    if (res.success) {
                        showSnackbar('Template deleted');
                        fetchData();
                    }
                } catch (error) {
                    showSnackbar('Error deleting template', 'error');
                }
            }
        });
    };

    // =====================================================
    // PREVIEW
    // =====================================================

    const interpolatePreview = (text, overrideEvent, overrideChannel) => {
        if (!text) return '';
        const event = overrideEvent || templateForm.event || editingTemplate?.event;
        const channel = overrideChannel || templateForm.channel || editingTemplate?.channel || 'order_status';
        const data = getPreviewData(event, channel);
        return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
            return data[key] !== undefined ? data[key] : match;
        });
    };

    const getEventSelectOptions = (channel) => {
        if (channel === 'free_cash') {
            return [{ value: '', label: '-- Select event --' }, ...FREE_CASH_EVENTS.map((e) => ({ value: e.key, label: e.label }))];
        }
        return [{ value: '', label: '-- None --' }, ...ORDER_STATUSES.map((s) => ({ value: s, label: s }))];
    };

    // =====================================================
    // RENDER
    // =====================================================

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    const orderStatusTemplates = templates.filter(t => t.isActive !== false && t.channel === 'order_status');
    const freeCashTemplates = templates.filter(t => t.isActive !== false && t.channel === 'free_cash');

    return (
        <div className="p-4 md:p-6">
            {/* Header — simplified (no back button), aligned with /branches style */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <FaBell className="text-blue-500" /> Push Notifications
                    </h1>
                    <p className="text-sm text-gray-500 mt-0.5">Configure automated & template-based push notifications</p>
                </div>
                {saving && (
                    <span className="text-sm text-blue-500 animate-pulse flex items-center gap-1">
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-blue-500"></div> Saving...
                    </span>
                )}
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-6 border-b border-gray-200">
                {[
                    { key: 'automation', label: 'Automation Settings', icon: <FaToggleOn /> },
                    { key: 'templates', label: 'Templates', icon: <FaEdit /> },
                ].map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                            activeTab === tab.key
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        {tab.icon} {tab.label}
                    </button>
                ))}
            </div>

            {/* ===================================== */}
            {/* AUTOMATION SETTINGS TAB */}
            {/* ===================================== */}
            {activeTab === 'automation' && (
                <div className="space-y-6">
                    {/* Order Status Automation */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gray-50">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-800">Order Status Notifications</h2>
                                <p className="text-sm text-gray-500">Automatically notify customers when their order status changes</p>
                            </div>
                            <button
                                onClick={() => handleToggleChannel('order_status')}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                    settings?.automations?.order_status?.enabled
                                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                }`}
                            >
                                {settings?.automations?.order_status?.enabled ? <FaToggleOn size={18} /> : <FaToggleOff size={18} />}
                                {settings?.automations?.order_status?.enabled ? 'Enabled' : 'Disabled'}
                            </button>
                        </div>

                        {settings?.automations?.order_status?.enabled && (
                            <div className="p-5">
                                <div className="grid gap-3">
                                    {ORDER_STATUSES.map(status => {
                                        const eventConfig = settings?.automations?.order_status?.events?.[status] || { active: false, templateId: null };
                                        const assignedTemplate = eventConfig.templateId
                                            ? templates.find(t => t._id === eventConfig.templateId || t._id?.toString() === eventConfig.templateId)
                                            : null;

                                        return (
                                            <motion.div
                                                key={status}
                                                layout
                                                className={`flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 rounded-lg border transition-colors ${
                                                    eventConfig.active ? 'border-blue-200 bg-blue-50/50' : 'border-gray-200 bg-gray-50/50'
                                                }`}
                                            >
                                                <div className="flex items-center gap-3 mb-2 sm:mb-0">
                                                    <button
                                                        onClick={() => handleToggleEvent('order_status', status)}
                                                        className={`p-1 rounded-full transition-colors ${
                                                            eventConfig.active ? 'text-green-500' : 'text-gray-300'
                                                        }`}
                                                    >
                                                        {eventConfig.active ? <FaToggleOn size={22} /> : <FaToggleOff size={22} />}
                                                    </button>
                                                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-600'}`}>
                                                        {status}
                                                    </span>
                                                </div>

                                                <div className="flex items-center gap-2 w-full sm:w-auto">
                                                    <Select
                                                        className="react-select-container"
                                                        classNamePrefix="react-select"
                                                        isDisabled={!eventConfig.active}
                                                        isClearable
                                                        placeholder="Select template..."
                                                        options={orderStatusTemplates.map(t => ({ value: t._id, label: t.name }))}
                                                        value={orderStatusTemplates.find(t => t._id === (eventConfig.templateId || '')) ? { value: (eventConfig.templateId || ''), label: (orderStatusTemplates.find(t => t._id === (eventConfig.templateId || ''))?.name || '') } : null}
                                                        onChange={(opt) => handleAssignTemplate('order_status', status, opt ? opt.value : '')}
                                                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                        menuPosition="fixed"
                                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                                    />
                                                    {assignedTemplate && (
                                                        <button
                                                            onClick={() => { setShowPreview(true); setEditingTemplate(assignedTemplate); }}
                                                            className="p-2 text-blue-500 hover:bg-blue-100 rounded-lg transition-colors"
                                                            title="Preview"
                                                        >
                                                            <FaEye />
                                                        </button>
                                                    )}
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>

                                {/* Info box */}
                                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
                                    <FaExclamationTriangle className="text-yellow-500 mt-0.5 shrink-0" />
                                    <div className="text-sm text-yellow-700">
                                        <strong>How it works:</strong> When an order's status is changed, if the corresponding event is active and has a template assigned,
                                        a push notification will be automatically sent to the customer's device. Notifications are also stored in-app.
                                    </div>
                                </div>

                                {/* Available Variables */}
                                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                    <h4 className="text-sm font-semibold text-blue-700 mb-2">Available Template Variables</h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                                        {TEMPLATE_VARIABLES.map(v => (
                                            <div key={v.var} className="flex items-center gap-2 text-xs">
                                                <code className="bg-blue-100 px-2 py-0.5 rounded text-blue-800 font-mono">{v.var}</code>
                                                <span className="text-blue-600">{v.desc}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Free Cash Automation */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gray-50">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-800">Free Cash Notifications</h2>
                                <p className="text-sm text-gray-500">Notify customers when Free Cash is loaded or about to expire</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => handleToggleChannel('free_cash')}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                    settings?.automations?.free_cash?.enabled
                                        ? 'bg-teal-100 text-teal-700 hover:bg-teal-200'
                                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                }`}
                            >
                                {settings?.automations?.free_cash?.enabled ? <FaToggleOn size={18} /> : <FaToggleOff size={18} />}
                                {settings?.automations?.free_cash?.enabled ? 'Enabled' : 'Disabled'}
                            </button>
                            {/* <h1 className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-500">Coming Soon</h1> */}
                        </div>

                        {settings?.automations?.free_cash?.enabled && (
                            <div className="p-5 space-y-3">
                                {FREE_CASH_EVENTS.map((fcEvent) => {
                                    const eventConfig = settings?.automations?.free_cash?.events?.[fcEvent.key] || { active: false, templateId: null };
                                    const assignedTemplate = eventConfig.templateId
                                        ? freeCashTemplates.find(t => t._id === eventConfig.templateId || t._id?.toString() === eventConfig.templateId)
                                        : null;

                                    return (
                                        <motion.div
                                            key={fcEvent.key}
                                            layout
                                            className={`p-4 rounded-lg border transition-colors ${
                                                eventConfig.active ? 'border-teal-200 bg-teal-50/50' : 'border-gray-200 bg-gray-50/50'
                                            }`}
                                        >
                                            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                                                <div className="flex items-start gap-3 flex-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleToggleEvent('free_cash', fcEvent.key)}
                                                        className={`p-1 rounded-full transition-colors shrink-0 ${
                                                            eventConfig.active ? 'text-teal-600' : 'text-gray-300'
                                                        }`}
                                                    >
                                                        {eventConfig.active ? <FaToggleOn size={22} /> : <FaToggleOff size={22} />}
                                                    </button>
                                                    <div>
                                                        <p className="text-sm font-semibold text-gray-800">{fcEvent.label}</p>
                                                        <p className="text-xs text-gray-500 mt-0.5">{fcEvent.description}</p>
                                                        {fcEvent.key === 'expiry_reminder' && eventConfig.active && (
                                                            <div className="flex items-center gap-2 mt-2">
                                                                <label className="text-xs text-gray-600 font-medium">Notify before expiry:</label>
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    max="10080"
                                                                    value={eventConfig.minutesBefore ?? 3}
                                                                    onChange={(e) => handleExpiryMinutesChange(e.target.value)}
                                                                    className="w-20 px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
                                                                />
                                                                <span className="text-xs text-gray-500">minutes</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-2 w-full lg:w-auto lg:min-w-[280px]">
                                                    <Select
                                                        className="react-select-container flex-1"
                                                        classNamePrefix="react-select"
                                                        isDisabled={!eventConfig.active}
                                                        isClearable
                                                        placeholder="Select template..."
                                                        options={freeCashTemplates.map(t => ({ value: t._id, label: t.name }))}
                                                        value={assignedTemplate ? { value: assignedTemplate._id, label: assignedTemplate.name } : null}
                                                        onChange={(opt) => handleAssignTemplate('free_cash', fcEvent.key, opt ? opt.value : '')}
                                                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                        menuPosition="fixed"
                                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                                    />
                                                    {assignedTemplate && (
                                                        <button
                                                            type="button"
                                                            onClick={() => { setShowPreview(true); setEditingTemplate(assignedTemplate); }}
                                                            className="p-2 text-teal-600 hover:bg-teal-100 rounded-lg transition-colors"
                                                            title="Preview"
                                                        >
                                                            <FaEye />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </motion.div>
                                    );
                                })}

                                <div className="mt-4 p-4 bg-teal-50 border border-teal-200 rounded-lg flex items-start gap-3">
                                    <FaExclamationTriangle className="text-teal-600 mt-0.5 shrink-0" />
                                    <div className="text-sm text-teal-800">
                                        <strong>How it works:</strong> Segment and Custom loads send only to customers who actually received the credit.
                                        Expiry reminders are scheduled per credit and sent once, exactly the configured minutes before that credit expires.
                                    </div>
                                </div>

                                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                    <h4 className="text-sm font-semibold text-blue-700 mb-2">Free Cash Template Variables</h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                                        {FREE_CASH_TEMPLATE_VARIABLES.map(v => (
                                            <div key={v.var} className="flex items-center gap-2 text-xs">
                                                <code className="bg-blue-100 px-2 py-0.5 rounded text-blue-800 font-mono">{v.var}</code>
                                                <span className="text-blue-600">{v.desc}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ===================================== */}
            {/* TEMPLATES TAB */}
            {/* ===================================== */}
            {activeTab === 'templates' && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-800">Notification Templates</h2>
                        <button
                            onClick={handleCreateTemplate}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
                        >
                            <FaPlus /> New Template
                        </button>
                    </div>

                    {templates.filter(t => t.isActive !== false).length === 0 ? (
                        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
                            <FaBell className="mx-auto text-4xl text-gray-300 mb-3" />
                            <h3 className="text-lg font-medium text-gray-600">No templates yet</h3>
                            <p className="text-sm text-gray-400 mt-1">Create your first notification template to get started</p>
                            <button
                                onClick={handleCreateTemplate}
                                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600"
                            >
                                <FaPlus className="inline mr-1" /> Create Template
                            </button>
                        </div>
                    ) : (
                        <div className="grid gap-3">
                            {templates.filter(t => t.isActive !== false).map(template => (
                                <motion.div
                                    key={template._id}
                                    layout
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow"
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="font-semibold text-gray-800">{template.name}</h3>
                                                {template.isDefault && (
                                                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">Default</span>
                                                )}
                                                <span className="px-2 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-full font-medium capitalize">{template.channel?.replace('_', ' ')}</span>
                                                {template.event && (
                                                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${STATUS_COLORS[template.event] || 'bg-gray-100 text-gray-600'}`}>
                                                        {template.event}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                                                <p className="text-sm font-medium text-gray-700">{template.title}</p>
                                                <p className="text-sm text-gray-500 mt-1">{template.body}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 ml-3">
                                            <button
                                                onClick={() => { setEditingTemplate(template); setShowPreview(true); }}
                                                className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                                                title="Preview"
                                            >
                                                <FaEye />
                                            </button>
                                            <button
                                                onClick={() => handleEditTemplate(template)}
                                                className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                                                title="Edit"
                                            >
                                                <FaEdit />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteTemplate(template._id)}
                                                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                title="Delete"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ===================================== */}
            {/* TEMPLATE CREATE/EDIT MODAL */}
            {/* ===================================== */}
            <AnimatePresence>
                {showTemplateModal && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                        onClick={() => setShowTemplateModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between p-5 border-b border-gray-200">
                                <h3 className="text-lg font-bold text-gray-800">
                                    {editingTemplate ? 'Edit Template' : 'Create New Template'}
                                </h3>
                                <button onClick={() => setShowTemplateModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                                    <FaTimes className="text-gray-400" />
                                </button>
                            </div>

                            <div className="p-5 space-y-4">
                                {/* Template Name */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
                                    <input
                                        type="text"
                                        value={templateForm.name}
                                        onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="e.g., Order Shipped Notification"
                                    />
                                </div>

                                {/* Channel & Event */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Channel *</label>
                                        <Select
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            options={CHANNELS.map(ch => ({ value: ch.value, label: ch.label }))}
                                            value={CHANNELS.find(c => c.value === templateForm.channel) ? { value: templateForm.channel, label: CHANNELS.find(c => c.value === templateForm.channel).label } : null}
                                            onChange={(opt) => setTemplateForm({
                                                ...templateForm,
                                                channel: opt ? opt.value : '',
                                                event: '',
                                                deepLink: opt?.value === 'free_cash'
                                                    ? { screen: 'FreeCashWallet', params: {} }
                                                    : templateForm.deepLink,
                                            })}
                                            isClearable={false}
                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                            menuPosition="fixed"
                                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Event / Status</label>
                                        <Select
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            options={getEventSelectOptions(templateForm.channel)}
                                            value={templateForm.event
                                                ? { value: templateForm.event, label: getEventSelectOptions(templateForm.channel).find(o => o.value === templateForm.event)?.label || templateForm.event }
                                                : { value: '', label: templateForm.channel === 'free_cash' ? '-- Select event --' : '-- None --' }}
                                            onChange={(opt) => setTemplateForm({
                                                ...templateForm,
                                                event: opt ? opt.value : '',
                                                deepLink: templateForm.channel === 'free_cash' || opt?.value
                                                    ? { screen: 'FreeCashWallet', params: {} }
                                                    : templateForm.deepLink,
                                            })}
                                            isClearable
                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                            menuPosition="fixed"
                                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                        />
                                    </div>
                                </div>

                                {/* Notification Title */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Notification Title *</label>
                                    <input
                                        type="text"
                                        value={templateForm.title}
                                        onChange={(e) => setTemplateForm({ ...templateForm, title: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        placeholder="e.g., Order {{orderNumber}} has been {{statusTo}}!"
                                    />
                                </div>

                                {/* Notification Body */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Notification Body *</label>
                                    <textarea
                                        value={templateForm.body}
                                        onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                                        rows={3}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                                        placeholder="e.g., Hi {{customerName}}, your order {{orderNumber}} is now {{statusTo}}. Track your order for updates!"
                                    />
                                </div>

                                {templateForm.channel === 'free_cash' && (
                                    <div className="p-3 bg-teal-50 border border-teal-100 rounded-lg">
                                        <p className="text-xs font-semibold text-teal-800 mb-2">Free Cash variables</p>
                                        <div className="flex flex-wrap gap-1">
                                            {FREE_CASH_TEMPLATE_VARIABLES.map((v) => (
                                                <code key={v.var} className="text-[10px] bg-white px-1.5 py-0.5 rounded border border-teal-100 text-teal-700">{v.var}</code>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Image URL */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Image URL (Optional)</label>
                                    <input
                                        type="text"
                                        value={templateForm.imageUrl}
                                        onChange={(e) => setTemplateForm({ ...templateForm, imageUrl: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        placeholder="https://example.com/image.png"
                                    />
                                </div>

                                {/* Default toggle */}
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => setTemplateForm({ ...templateForm, isDefault: !templateForm.isDefault })}
                                        className={`p-1 rounded-full transition-colors ${
                                            templateForm.isDefault ? 'text-green-500' : 'text-gray-300'
                                        }`}
                                    >
                                        {templateForm.isDefault ? <FaToggleOn size={22} /> : <FaToggleOff size={22} />}
                                    </button>
                                    <span className="text-sm text-gray-600">Set as default template for this channel/event</span>
                                </div>

                                {/* Preview section in modal */}
                                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                        <FaMobileAlt className="text-blue-500" /> Notification Preview
                                    </h4>
                                    <div className="bg-white rounded-xl shadow-md p-4 max-w-xs mx-auto border border-gray-100">
                                        <div className="flex items-start gap-3">
                                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                                                <FaBell className="text-blue-500" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-gray-800 truncate">
                                                    {interpolatePreview(templateForm.title) || 'Notification Title'}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-1 line-clamp-3">
                                                    {interpolatePreview(templateForm.body) || 'Notification body will appear here...'}
                                                </p>
                                                <p className="text-xs text-gray-300 mt-2">Just now</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-200">
                                <button
                                    onClick={() => setShowTemplateModal(false)}
                                    className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveTemplate}
                                    disabled={saving}
                                    className="flex items-center gap-2 px-5 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors disabled:opacity-50"
                                >
                                    <FaSave /> {saving ? 'Saving...' : (editingTemplate ? 'Update' : 'Create')}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ===================================== */}
            {/* PREVIEW MODAL */}
            {/* ===================================== */}
            <AnimatePresence>
                {showPreview && editingTemplate && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                        onClick={() => { setShowPreview(false); setEditingTemplate(null); }}
                    >
                        <motion.div
                            initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                                <FaMobileAlt className="text-blue-500" /> Push Notification Preview
                            </h3>
                            <div className="bg-gray-900 rounded-xl p-4 text-white">
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                                        <FaBell className="text-white text-xs" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs text-gray-400 mb-0.5">db ecart • now</p>
                                        <p className="text-sm font-semibold">{interpolatePreview(editingTemplate.title, editingTemplate.event, editingTemplate.channel)}</p>
                                        <p className="text-xs text-gray-300 mt-1">{interpolatePreview(editingTemplate.body, editingTemplate.event, editingTemplate.channel)}</p>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => { setShowPreview(false); setEditingTemplate(null); }}
                                className="w-full mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors"
                            >
                                Close Preview
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Snackbar */}
            <AnimatePresence>
                {snackbar.show && (
                    <motion.div
                        initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 50 }}
                        className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-xl shadow-lg text-sm font-medium flex items-center gap-2 ${
                            snackbar.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
                        }`}
                    >
                        {snackbar.type === 'error' ? <FaExclamationTriangle /> : <FaCheckCircle />}
                        {snackbar.message}
                    </motion.div>
                )}
            </AnimatePresence>

            <ConfirmModal
                visible={confirmModal.visible}
                message={confirmModal.message}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal(prev => ({ ...prev, visible: false }))}
                cancelButtonText="Cancel"
                confirmButtonText="Delete"
            />
        </div>
    );
};

export default NotificationTemplates;
